import type { ReactNode } from 'react';
import styles from './Field.module.css';

/** Shared class for inputs, selects and textareas rendered inside a Field. */
export const controlClass = styles.control;

export interface FieldControlProps {
  id: string;
  'aria-invalid': true | undefined;
  'aria-describedby': string | undefined;
}

export interface FieldProps {
  /** Also used to build the control and error element ids. */
  name: string;
  label: string;
  error?: string | undefined;
  optional?: boolean;
  /** Receives the ids and ARIA wiring the control must spread onto itself. */
  children: (props: FieldControlProps) => ReactNode;
}

/**
 * Label, control and error message wired together: one place that guarantees
 * every input is labelled and every error is announced.
 */
export function Field({ name, label, error, optional = false, children }: FieldProps) {
  const id = `field-${name}`;
  const errorId = `${id}-error`;

  return (
    <div className={styles.field}>
      <span className={styles.labelRow}>
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
        {optional && <span className={styles.optional}>optional</span>}
      </span>

      {children({
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': error ? errorId : undefined,
      })}

      {error && (
        <span className={styles.error} id={errorId}>
          {error}
        </span>
      )}
    </div>
  );
}
